import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { CapacitorDeviceService, ChangePasswordDTO, RegisterWithEmailDTO, ViispTokenResponse } from '@ridango/core';

import { Observable } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { PhoneNumberUtil } from 'google-libphonenumber';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

@Injectable({
    providedIn: 'root'
})
export class Authenticator {
    challengePollIntervalDelayMs = 5000;
    private readonly rememberMeValueLocalStorageKey = this.env.APP_NAME + '-remember-me-value';

    // moved here from core/auth.service
    constructor(
        @Inject('ENV') private env: Env,
        private http: HttpClient,
        private capacitorDeviceService: CapacitorDeviceService
    ) {}

    signUpUserWithCredentials(registerDTO: RegisterWithEmailDTO) {
        if (this.env.WMB?.AUTH) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/register`;
            const PhoneNumberProto = phoneNumberUtil.parse(registerDTO.phoneNumber);
            const countryCode = `+${PhoneNumberProto.getCountryCode()}`;
            const phoneNumberWithoutCountryCode = registerDTO.phoneNumber?.replace(countryCode, '');
            const payload = {
                clientId: this.env.WMB.AUTH.CLIENT_ID,
                redirectUri: this.env.BASE_URL,
                email: registerDTO.email,
                password: registerDTO.password,
                countryCode: countryCode,
                phoneNumber: phoneNumberWithoutCountryCode,
                firstName: registerDTO.firstName ?? registerDTO.name,
                lastName: registerDTO.lastName,
                personalCode: registerDTO.personalCode,
                lang: registerDTO.language
            };
            return this.http.post(url, payload);
        }
        const headers = new HttpHeaders({ 'Content-Language': registerDTO.language });
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/${this.env.OAUTH_CLIENT_ID}`;
        let payload: any = {};
        payload = {
            username: registerDTO.email,
            password: registerDTO.password,
            name: registerDTO.name,
            callback_url: registerDTO.callbackUrl
        };
        if (registerDTO.terms) {
            payload = {
                username: registerDTO.email,
                password: registerDTO.password,
                name: registerDTO.name,
                terms_version: registerDTO.terms.id,
                callback_url: registerDTO.callbackUrl
            };
        }
        return this.http.post(url, payload, { headers: headers });
    }

    verifyAccount(token: string) {
        if (this.env.IS_NEW_AFC && this.env.WMB?.AUTH) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/verify-account`;
            const payload = {
                verificationToken: token,
                clientId: this.env.WMB.AUTH.CLIENT_ID
            };
            return this.http.post(url, payload);
        }
        if (/^[a-f0-9]{32}$/.test(token)) {
            const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/auth`;
            return this.http.post(url, { type: 'verify_account', token });
        }
        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/verify/${this.env.OAUTH_CLIENT_ID}`;
        return this.http.post(url, token);
    }

    submitCredentials(username: string, password: string, lang: string): Observable<any> {
        const url = `${this.env.OAUTH_URL}/auth/oauth2/token`;
        const headers = new HttpHeaders({ 'Content-Language': lang });
        return this.capacitorDeviceService.getAuthDeviceContext().pipe(
            switchMap((deviceContext) => {
                const payload = {
                    grant_type: 'password',
                    username: username,
                    password: password,
                    client_id: this.env.OAUTH_CLIENT_ID,
                    channel: this.env.CHANNEL,
                    customerDevice: {
                        deviceId: deviceContext.deviceId,
                        model: deviceContext.deviceModel,
                        osVersion: deviceContext.osVersion
                    }
                };

                return this.http.post(url, payload, { headers: headers });
            })
        );
    }

    submitEmailCredentials(email: string, password: string) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/login`;
        return this.capacitorDeviceService.getAuthDeviceContext().pipe(
            switchMap((deviceContext) => {
                const payload = {
                    clientId: this.env.WMB?.AUTH?.CLIENT_ID,
                    email: email,
                    password: password,
                    channel: this.env.CHANNEL,
                    customerDevice: {
                        deviceId: deviceContext.deviceId,
                        model: deviceContext.deviceModel,
                        osVersion: deviceContext.osVersion
                    }
                };
                return this.http.post<{ accessToken: string; refreshToken: string; tokenType: string }>(url, payload);
            })
        );
    }

    endSession(refreshToken: string) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/logout`;
        const payload = {
            clientId: this.env.WMB?.AUTH?.CLIENT_ID,
            refreshToken: refreshToken
        };
        return this.http.post(url, payload);
    }

    submitIdCardAuth(token: any, nonce: string) {
        const url = `${this.env.API_GATEWAY_URL}/identity/v1/auth/region/${this.env.REGION_ID}/validator/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const payload = { token, nonce };
        return this.http.post(url, payload, { headers });
    }

    checkAuthChallengeStatus(authMethod: string, hash: string): Observable<any> {
        const url = `${this.env.API_GATEWAY_URL}/identity/v1/${authMethod}/challenge/poll`;
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const payload = {
            hash: hash
        };

        return this.http.post(url, payload, { headers: headers }).pipe(delay(this.challengePollIntervalDelayMs));
    }

    fetchAccessToken(
        provider: string,
        authorizationCode: string,
        termsVersion: any = null,
        name: string = null,
        ondemandRequest: boolean = null,
        channel: string = this.env.CHANNEL,
        lang: any = null
    ) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/auth`;
        let payload: any = {};
        payload = {
            type: provider,
            token: authorizationCode,
            region_mask: this.env.REGION_ID,
            channel: channel
        };

        if (ondemandRequest) {
            payload.ondemand_request = ondemandRequest;
        }

        if (termsVersion) {
            payload.terms_version = termsVersion;
        }

        if (lang) {
            payload.lang = lang;
        }

        if (name) {
            payload.name = name;
        }

        return this.http.post(url, payload).pipe(map((data) => data));
    }

    fetchViispToken(ticket: string): Observable<ViispTokenResponse> {
        const baseUrl = `${this.env.API_GATEWAY_URL}/identity/v1/viisp/auth/ticket`;
        return this.http.get<ViispTokenResponse>(`${baseUrl}/${ticket}/token/${this.env.REGION_ID}`);
    }

    updateAgreeTerms(termsId, code, ondemandRequest: boolean = null) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/terms-and-conditions/agree`;
        let payload: any = {};
        payload = {
            code: code,
            terms_id: termsId
        };

        if (ondemandRequest) {
            payload.ondemand_request = ondemandRequest;
        }

        return this.http.put(url, payload);
    }

    sendResetEmail(email: string, lang: string, callback: string) {
        if (this.env.IS_NEW_AFC) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/forgot-password`;
            const payload = {
                email: email,
                lang: lang
            };
            return this.http.post(url, payload);
        }

        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/reset/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders().set('Content-Language', lang);
        const options = {
            headers: headers
        };
        const payload = {
            username: email,
            password: '',
            callback_url: callback
        };

        return this.http.post(url, payload, options);
    }

    sendNewPassword(password: string, token: string, lang: string) {
        if (this.env.IS_NEW_AFC) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/reset-password`;
            const payload = {
                resetToken: token,
                newPassword: password
            };
            return this.http.post(url, payload);
        }

        const url = `${this.env.OAUTH_URL}/auth/oauth2/signup/reset/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`).set('Content-Language', lang);
        const options = {
            headers: headers
        };
        const payload = {
            username: '',
            password: password
        };

        return this.http.post(url, payload, options);
    }

    changePassword(payload: ChangePasswordDTO) {
        if (this.env.IS_NEW_AFC) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/auth/change-password`;
            return this.http.put(url, payload);
        }
        const url = `${this.env.OAUTH_URL}/auth/oauth2/change/password/${this.env.OAUTH_CLIENT_ID}`;
        const headers = new HttpHeaders()
            .set('Accept', `application/json`)
            .set('Content-Type', 'application/json; charset=utf-8');
        const options = {
            headers: headers
        };

        return this.http.put(url, payload, options);
    }

    getRememberMeValue(): string {
        return localStorage.getItem(this.rememberMeValueLocalStorageKey);
    }

    setRememberMeValue(value?: string) {
        if (value) {
            localStorage.setItem(this.rememberMeValueLocalStorageKey, value);
        } else {
            localStorage.removeItem(this.rememberMeValueLocalStorageKey);
        }
    }
}
